import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import axios from "axios";
import VAULT_ABI from "../abis/Vault.json";
import ACCOUNT_ABI from "../abis/Account.json";
import { tokens, USDC_CONTRACT_ADDRESS, vaultAddress } from "./constants";
import { RPC_URLS } from "./rpcUrls";
import { ethers } from "ethers";
import { Enclave } from "enclavemoney-web-experimental";

const headers = new Headers();
const enclave = new Enclave(process.env.REACT_APP_ENCLAVE_SDK_KEY);
headers.append("Content-Type", "application/json");
headers.append("Authorization", `${process.env.REACT_APP_ENCLAVE_SDK_KEY}`);

const BASE_URL = process.env.REACT_APP_BASE_URL;
export const createAccount = async (username, inviteCode) => {
  const resp = await enclave.getPasskeyAccountRegistrationOptions(
    username,
    inviteCode
  );

  let attResp;
  try {
    attResp = await enclave.startPasskeyAccountRegistration(resp);
  } catch (error) {
    throw error;
  }

  console.log("\n\nattResp: ", attResp);

  const verificationResp =
    await enclave.submitPasskeyAccountRegistrationResponse({
      username,
      attResp,
    });

  return verificationResp;
};

export const generateMultiTransactionOptions = async (
  username,
  transactionDetails,
  network,
  label = undefined,
  gasMode = "ABSTRACTED_INTERNAL",
  feeToken = undefined,
  feeTokenAmount = undefined,
  orderData,
  metaData = null
) => {
  const resp = await enclave.getPasskeyTransactionOptions({
    username,
    transactionDetails,
    network,
    gasMode,
    orderData,
    label,
    feeToken,
    feeTokenAmount,
    metaData,
  });

  return resp;
};

export const verifyMultiTransaction = async (username, attResp) => {
  const verificationResp = await enclave.submitPasskeyTransactionResponse({
    username,
    attResp,
  });

  // The response is already a JSON object with transaction details
  if (!verificationResp || !verificationResp.txnHash) {
    throw new Error("Invalid transaction response");
  }

  return verificationResp;
};

export const submitMultiTransaction = async (
  username,
  transactionDetails,
  network,
  label = undefined,
  gasMode = "ABSTRACTED_INTERNAL",
  feeToken = undefined,
  feeTokenAmount = undefined,
  orderData,
  metaData = null
) => {
  console.log(
    "submitTransaction: ",
    username,
    transactionDetails,
    network,
    orderData
  );

  let attResp;
  try {
    const opts = await generateMultiTransactionOptions(
      username,
      transactionDetails,
      network,
      label,
      gasMode,
      feeToken,
      feeTokenAmount,
      orderData,
      metaData
    );
    console.log("Registration Options: ", opts);
    attResp = await startAuthentication(opts);
  } catch (error) {
    throw error;
  }

  console.log("\n\nattResp: ", attResp);

  const verificationJSON = await enclave.submitPasskeyTransactionResponse({
    username,
    attResp,
  });
  return verificationJSON;
};

export const computeQuote = async (
  walletAddress,
  outputNetwork,
  amount,
  type,
  limit
) => {
  const resp = await enclave.computeQuote({
    walletAddress,
    outputNetwork,
    amount,
    type,
    limit,
  });

  return resp;
};

export const getSmartBalance = async (walletAddress) => {
  const resp = await enclave.getSmartBalanceObject(walletAddress);

  return resp;
};

export const get0Xquote = async (
  sellToken,
  buyToken,
  sellAmount,
  takerAddress,
  chainId
) => {
  const resp = await axios.get(
    `${BASE_URL}/api/zerox/quote?sellToken=${sellToken}&buyToken=${buyToken}&sellAmount=${sellAmount}&takerAddress=${takerAddress}&chainId=${chainId}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${process.env.REACT_APP_ENCLAVE_SDK_KEY}`,
      },
    }
  );

  return resp.data;
};

export const getWithdrawFromVaultCalldata = (amount, chainId) => {
  const provider = new ethers.JsonRpcProvider(RPC_URLS[chainId]);
  const usdcAddress = tokens["USDC"][chainId];
  const vault = new ethers.Contract(vaultAddress, VAULT_ABI, provider);
  // ABI encode withdraw function in Vault contract
  const encodedData = vault.interface.encodeFunctionData("withdraw", [
    usdcAddress,
    amount,
  ]);
  return encodedData;
};

export const getDepositToVaultCalldata = (amount, chainId) => {
  const provider = new ethers.JsonRpcProvider(RPC_URLS[chainId]);
  const usdcAddress = tokens["USDC"][chainId];
  const vault = new ethers.Contract(vaultAddress, VAULT_ABI, provider);
  // ABI encode withdraw function in Vault contract
  const encodedData = vault.interface.encodeFunctionData("deposit", [
    usdcAddress,
    amount,
  ]);
  return encodedData;
};

export const getDepositAllToVaultCalldata = (chainId) => {
  const provider = new ethers.JsonRpcProvider(RPC_URLS[chainId]);
  const usdcAddress = tokens["USDC"][chainId];
  const vault = new ethers.Contract(vaultAddress, VAULT_ABI, provider);
  // ABI encode withdraw function in Vault contract
  const encodedData = vault.interface.encodeFunctionData("depositAll", [
    usdcAddress,
  ]);
  return encodedData;
};

export const getSmartBalanceConvertCalldata = (walletAddress, chainId) => {
  const provider = new ethers.JsonRpcProvider(RPC_URLS[chainId]);
  const usdcAddress = tokens["USDC"][chainId];
  const account = new ethers.Contract(walletAddress, ACCOUNT_ABI, provider);
  // ABI encode withdraw function in Vault contract
  const encodedData = account.interface.encodeFunctionData(
    "smartBalanceConvert",
    [usdcAddress]
  );
  return encodedData;
};

export const getMultiGasFees = async (
  username,
  walletAddress,
  transactionDetails,
  network,
  gasMode,
  orderData
) => {
  try {
    const resp = await enclave.getGasFee({
      username,
      walletAddress,
      transactionDetails,
      network,
      gasMode,
      orderData,
    });

    if (resp.error) {
      throw new Error(resp.error);
    }

    return resp;
  } catch (error) {
    console.error("Error in getMultiGasFees:", error);

    // Throw the original error with all its properties
    if (error.response?.data) {
      throw {
        ...error,
        message: error.response.data.message || error.message,
        error: error.response.data.error || error.error,
        data: error.response.data.data || error.data,
      };
    }

    throw error;
  }
};

export const getTokenPriceChart = async (
  address,
  chainId,
  resolution = "1D"
) => {
  const timeToLocal = (originalTime) => {
    const d = new Date(originalTime * 1000);
    return (
      Date.UTC(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
        d.getSeconds(),
        d.getMilliseconds()
      ) / 1000
    );
  };

  try {
    const response = await axios.get(`${BASE_URL}/token/price-chart`, {
      params: {
        address,
        chainId,
        resolution,
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `${process.env.REACT_APP_ENCLAVE_SDK_KEY}`,
      },
    });

    if (!response.data || !response.data.success) {
      throw new Error("Invalid data format received from API");
    }

    const { o, h, l, c, v, t } = response.data.data;

    // Check if the required arrays exist
    if (!o || !h || !l || !c || !v || !t) {
      return { noData: true };
    }

    // Check if any of the price values are null
    const hasNullValues =
      c.some((val) => val === null) ||
      o.some((val) => val === null) ||
      h.some((val) => val === null) ||
      l.some((val) => val === null);

    if (hasNullValues) {
      console.log("Found null values in price data");
      return { noData: true };
    }

    // Validate arrays have the same length
    if (
      !Array.isArray(o) ||
      !Array.isArray(h) ||
      !Array.isArray(l) ||
      !Array.isArray(c) ||
      !Array.isArray(v) ||
      !Array.isArray(t) ||
      o.length !== h.length ||
      h.length !== l.length ||
      l.length !== c.length ||
      c.length !== v.length ||
      v.length !== t.length
    ) {
      throw new Error(
        "Invalid data structure: arrays must have the same length"
      );
    }

    // Combine the arrays into the format expected by TradingView
    return t.map((timestamp, index) => ({
      time: timeToLocal(timestamp),
      open: o[index],
      high: h[index],
      low: l[index],
      close: c[index],
      volume: v[index],
    }));
  } catch (error) {
    console.error("Error fetching token price chart:", error);
    throw error;
  }
};
